import { JSX } from "react";
import { useDevice } from "hooks";
import { DropdownOption, InputFieldError } from "@finbackoffice/site-core";
import CustomSelect from "../custom-select/CustomSelect";
import Dropdown from "../dropdown/Dropdown";

/**
 * Universal dropdown component that renders CustomSelect for desktop and native Dropdown for mobile
 * This provides a consistent API while adapting to the device type
 */
export interface IUniversalDeviceDropdownProps<T extends string, P extends boolean = false> {
    /** Dropdown options to display */
    options: DropdownOption[];
    /** CSS class for the wrapper */
    wrapperClassname?: string;
    /** Field error object */
    error?: InputFieldError;
    /** Whether the field is required */
    required?: boolean;
    /** Field name (used for native dropdown) */
    name?: string;
    /** Current selected value(s) */
    value: P extends true ? T[] : T;
    /** Callback when selection changes */
    onChange: (value: P extends true ? T[] : T) => void;
    /** Placeholder text (translation key) */
    placeholder?: string;
    /** Whether the field is disabled */
    disabled?: boolean;
    /** Whether to allow multiple selections */
    multiple?: P;
    /** Loading state */
    loading?: boolean;
    /** Custom render function for items (CustomSelect only) */
    renderItem?: (item: DropdownOption) => JSX.Element;
    /** Label for the field */
    label?: string;
    /** Test ID for testing */
    testId?: string;
    /** Modal type (CustomSelect only) */
    type?: string;
    /** SVG source (CustomSelect only) */
    svgSrc?: string;
    /** Variant style (CustomSelect only) */
    variantStyle?: "default" | "gradient";
}

const UniversalDeviceDropdown = <T extends string, P extends boolean = false>({
    options,
    wrapperClassname,
    error,
    required,
    name = "dropdown",
    value,
    onChange,
    placeholder,
    disabled,
    multiple,
    loading,
    renderItem,
    label,
    testId,
    type,
    svgSrc,
    variantStyle = "default",
}: IUniversalDeviceDropdownProps<T, P>) => {
    const { isMobileDevice } = useDevice();

    // For desktop (or non-mobile), use CustomSelect which has better UI/UX
    if (!isMobileDevice) {
        return (
            <CustomSelect
                data={options}
                wrapperClassname={wrapperClassname}
                selected={value}
                onChange={onChange}
                variant="dropdown"
                placeholder={placeholder}
                error={error}
                required={required}
                disabled={disabled}
                multiple={multiple}
                loading={loading}
                renderItem={renderItem}
                label={label}
                testId={testId}
                type={type}
                svgSrc={svgSrc}
                variantStyle={variantStyle}
            />
        );
    }

    // For mobile, use native dropdown for better mobile experience
    // Wrap onChange to handle native select event
    const handleNativeChange = (e: React.FormEvent<HTMLSelectElement>) => {
        const target = e.target as HTMLSelectElement;
        onChange(target.value as P extends true ? T[] : T);
    };

    return (
        <Dropdown
            name={name}
            onChange={handleNativeChange}
            value={value as string} // Native select works with string
            wrapperClassname={wrapperClassname}
            options={options}
            error={error}
            placeholder={placeholder || undefined}
            required={required}
            disabled={disabled}
            label={label}
            data-testid={testId}
        />
    );
};

export default UniversalDeviceDropdown;
